import { Component, EventEmitter, Input, Output } from '@angular/core';
import { LicenseBatchDto } from 'src/app/models/license.model';

@Component({
  selector: 'app-license-batch-card',
  templateUrl: './license-batch-card.component.html',
  styleUrls: ['./license-batch-card.component.scss'],
  standalone: false,
})
export class LicenseBatchCardComponent {
  @Input() batch: LicenseBatchDto | null = null;
  @Input() showEditAction = true;
  @Input() showManageDevicesAction = true;
  @Input() showDeleteAction = true;
  @Output() viewBatch = new EventEmitter<string>();
  @Output() editBatch = new EventEmitter<string>();
  @Output() manageDevices = new EventEmitter<string>();
  @Output() manageUsers = new EventEmitter<string>();
  @Output() deleteBatch = new EventEmitter<string>();

  onView(): void {
    const batchId = this.batch?.id;
    if (!batchId) {
      return;
    }
    this.viewBatch.emit(batchId);
  }

  onEdit(event: Event): void {
    event.stopPropagation();
    const batchId = this.batch?.id;
    if (!batchId) {
      return;
    }
    this.editBatch.emit(batchId);
  }

  onDelete(event: Event): void {
    event.stopPropagation();
    const batchId = this.batch?.id;
    if (!batchId) {
      return;
    }
    this.deleteBatch.emit(batchId);
  }

  onManageDevices(event: Event): void {
    event.stopPropagation();
    const batchId = this.batch?.id;
    if (!batchId) {
      return;
    }
    this.manageDevices.emit(batchId);
  }

  onManageUsers(event: Event): void {
    event.stopPropagation();
    const batchId = this.batch?.id;
    if (batchId) this.manageUsers.emit(batchId);
  }

  get isUserTarget(): boolean {
    return this.batch?.product?.target === 'USER';
  }

  formatDate(dateString?: string): string {
    if (!dateString) {
      return 'Not set';
    }

    const parsedDate = new Date(dateString);
    if (isNaN(parsedDate.getTime())) {
      return dateString;
    }

    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(parsedDate);
  }

  get batchTitle(): string {
    const batchName = this.batch?.name?.trim();
    if (batchName) {
      return batchName;
    }
    return 'License Batch';
  }

  get batchDescription(): string {
    return this.batch?.description?.trim() || 'No description provided.';
  }

  get productLabel(): string {
    const productName = this.batch?.product?.name?.trim();
    const productId = this.batch?.licensedProductId?.trim();
    if (productName) {
      return productName;
    }
    if (productId) {
      return productId;
    }
    return 'Not assigned';
  }

  get scopeLabel(): string {
    // A scope whose tag is gone from Spadok comes without a name
    return (
      this.batch?.scope?.name?.trim() ||
      this.batch?.scope?.id?.trim() ||
      'Not assigned'
    );
  }

  get componentNames(): string[] {
    const rawComponents =
      (this.batch?.product as { licensedComponents?: unknown[] } | undefined)
        ?.licensedComponents ?? [];
    return rawComponents
      .map((component) => {
        if (typeof component === 'string') {
          return component.trim();
        }
        if (component && typeof component === 'object') {
          const typedComponent = component as {
            componentName?: string;
            id?: string;
          };
          return (
            typedComponent.componentName?.trim() ||
            typedComponent.id?.trim() ||
            ''
          );
        }
        return '';
      })
      .filter((name) => !!name);
  }

  get visibleComponentNames(): string[] {
    return this.componentNames.slice(0, 5);
  }

  get hiddenComponentsCount(): number {
    return Math.max(
      0,
      this.componentNames.length - this.visibleComponentNames.length,
    );
  }

  get assignedDevicesCount(): number {
    const deviceIdsCount = this.batch?.licensedDeviceIds?.length ?? 0;
    if (deviceIdsCount > 0) {
      return deviceIdsCount;
    }
    return this.batch?.licensedDevices?.length ?? 0;
  }

  get assignedUsersCount(): number {
    return (
      this.batch?.licensedUserIds?.length ||
      this.batch?.licensedUsers?.length ||
      0
    );
  }
}
